import React, { useState } from "react";
import AdminAuthManager from "../../../services/dashboard/AdminAuthManager";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { adminLoginSuccess } from "../../../features/adminAuthSlice";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const response = await AdminAuthManager.login(form);
    const data = response?.responseData;

    if (!data?.token) throw new Error("No token returned from API");

    const token = data.token;
    const admin = data.userOrAdmin || {};
    const roles = data.roles || (admin.roles || []).map(r => r.name);
    const permissions =
      data.permissions ||
      (admin.roles || []).flatMap(r => (r.permissions || []).map(p => p.code));

    // Debug logging
    console.log('Login Response:', response);
    console.log('Data:', data);
    console.log('Admin:', admin);
    console.log('Roles extracted:', roles);
    console.log('Permissions extracted:', permissions);

    // ✅ store token for axiosInstance interceptor
    localStorage.setItem("adminAuthToken", token);

    // ✅ optional (for persistence or debugging)
    localStorage.setItem("adminData", JSON.stringify(admin));
    localStorage.setItem("adminRoles", JSON.stringify(roles));
    localStorage.setItem("adminPermissions", JSON.stringify(permissions));

    // ✅ dispatch Redux state
    dispatch(
      adminLoginSuccess({
        token,
        admin,
        roles,
        permissions,
      })
    );

    toast.success(`Welcome, ${admin.name || "Admin"}!`);
    navigate("/adminDashboard");
  } catch (err) {
    console.error("Login error:", err);
    toast.error("Invalid admin credentials");
  } finally {
    setLoading(false);
  }
};


  return (
    <div
      className="min-h-screen bg-cover bg-center relative flex items-center justify-center flex-col px-4"
      style={{ backgroundImage: "url('/images/mem.avif')" }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-40 z-0" />


<div className="absolute top-6 left-6 right-6 z-20 flex justify-between items-center">
  {/* Back Button (left) */}
  <a
    href="https://booksatdoorsteps.com"
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary to-primary-shade4 text-white font-medium shadow-md hover:scale-105 transition-transform duration-300"
  >
    <ArrowLeft size={18} />
    Back to Website
  </a>

  {/* Developed by (right) */}
  <div className="flex items-center gap-2 text-white text-sm font-nunito">
    <span>Developed by</span>
    <a
      href="https://applauseitsolutions.com"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-primary hover:underline"
    >
      <img
        src="/images/applauseLogo.png"
        alt="Developer Logo"
        className="h-10"
      />
    </a>
  </div>
</div>


      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6 py-10 backdrop-blur-glass bg-white/10 border border-white/20 rounded-2xl shadow-glass text-white">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/images/adminLogo.png" alt="Logo" className="h-12" />
        </div>

        {/* Heading */}
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold font-nunito text-white drop-shadow">
            Books At Doorsteps
          </h1>
          <p className="text-sm font-nunito text-white/80 mt-1">Admin Login</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 animate-slide-up">
          <div>
            <label className="block text-sm font-medium mb-2 text-white/80">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all font-nunito"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-white/80">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary transition pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-white/60 hover:text-white transition"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary-shade4 text-white py-3 rounded-lg font-nunito font-semibold transition-transform transform hover:scale-105 flex items-center justify-center shadow-md"
          >
            {loading ? (
              <ClipLoader size={20} color="#FFFFFF" className="mr-2" />
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>

      {/* Footer */}
      <div className="relative z-10 mt-8 text-center text-white/70 text-sm font-nunito space-y-1">
        <p>© {new Date().getFullYear()} Books At Doorsteps. All rights reserved.</p>
        <p>
          Need help?{" "}
          <a
            href="mailto:support@applausetechno.com"
            className="text-primary hover:underline"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
